// Third Party Plugins
import Image from 'next/image';
import React from 'react';
// Types
import type { ImageContainerProps } from './types';
// Styles
import { StyledFigure } from './styles';

// Function to extract width and height from the URL
function extractDimensionsFromUrl(imageUrl: string) {
  const dimensionRegex = /-(\d+)x(\d+)\./;
  const match = imageUrl.match(dimensionRegex);

  if (match) {
    const width = parseInt(match[1], 10);
    const height = parseInt(match[2], 10);
    return { width, height };
  }

  return { width: 500, height: 500 }; // Default size if dimensions aren't found
}

const ImageContainer: React.FC<ImageContainerProps> = ({
  width = 500, // Default width
  height = 500, // Default height
  image,
  alt,
  type,
  ...props
}) => {
  let content;

  // Determine the image URL based on the type of the image prop
  let imageUrl: string | undefined;
  if (typeof image === 'string') {
    imageUrl = image;
  } else if (image?.url) {
    imageUrl = image.url;
  } else if (image?.asset?.url) {
    imageUrl = image.asset.url;
  }

  // Extract the image dimensions from the URL if available
  let dimensions = { width, height }; // Use the default width and height

  if (imageUrl) {
    dimensions = extractDimensionsFromUrl(imageUrl);
  }

  // Calculate the maximum width and height, limiting width to 768px
  const maxWidth = Math.min(dimensions.width, 768);
  const maxHeight = (dimensions.height / dimensions.width) * maxWidth;

  // Handle the case for static and dynamic images
  if (imageUrl) {
    content = (
      <Image
        src={imageUrl}
        alt={alt}
        width={maxWidth} // Apply the limited width
        height={maxHeight} // Apply the dynamic height based on aspect ratio
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        style={{ objectFit: 'contain' }}
        {...props}
      />
    );
  } else {
    content = null;
  }

  // Render the content within a styled figure element with the same max size as the image
  return (
    <StyledFigure
      style={{
        width: '100%',
        maxWidth: '600px', // desktop cap
        margin: '0 auto',
        position: 'relative',
        aspectRatio: `${dimensions.width} / ${dimensions.height}`, // 🔥 keeps shape
      }}
    >
      {imageUrl && (
        <Image
          src={imageUrl}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 768px"
          style={{
            objectFit: 'contain',
          }}
          {...props}
        />
      )}
    </StyledFigure>
  );
};

export default ImageContainer;
